import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { buildGuestWhatsAppUrl } from "@/lib/whatsapp";
import { toast } from "sonner";
import { MessageCircle, RefreshCw, CalendarClock, Printer, Send } from "lucide-react";
import { Link } from "react-router-dom";

function reminderMessage(reservation, origin) {
  const cancel = `${origin}/r/cancel/${reservation.cancel_token || ""}`;
  const body =
    `Hello ${reservation.name}, this is The Barracks Bistro. A friendly reminder for your ` +
    `reservation tomorrow, ${reservation.date} at ${reservation.time} for ${reservation.party_size} pax. ` +
    `We look forward to hosting you. If plans change, please cancel here so we can free up the table: ${cancel}`;
  const phone = (reservation.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(body)}` : null;
}

export default function Reminders() {
  const [days, setDays] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState(() => JSON.parse(localStorage.getItem("tbb_reminders_sent") || "{}"));

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/reservations/reminders?days=${d}`);
      setItems(data);
    } catch { toast.error("Could not load reminders."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, reloadKey]);

  const markSent = (id) => {
    const next = { ...sent, [id]: new Date().toISOString() };
    setSent(next);
    localStorage.setItem("tbb_reminders_sent", JSON.stringify(next));
    toast.success("Marked as sent");
  };

  const label = days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;

  return (
    <div data-testid="staff-reminders">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Reminders</div>
          <h1 className="mt-2 text-4xl">Bookings {label}</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Confirmed reservations landing {label}. Click each row's WhatsApp button to send the
            guest a personalised day-before reminder — with their cancel link so no-shows shrink.
            Mark rows as "Sent" once you've hit send in WhatsApp.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]">
            {[
              [0, "Today"],
              [1, "Tomorrow"],
              [2, "In 2 days"],
              [7, "In a week"],
            ].map(([d, l]) => (
              <button
                key={d}
                data-testid={`window-${d}`}
                onClick={() => { setDays(d); setReloadKey((k) => k + 1); }}
                className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all ${days === d ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
              >{l}</button>
            ))}
          </div>
          <button data-testid="refresh" onClick={() => load()} className="p-2 rounded border border-[hsl(var(--rule))]" title="Refresh"><RefreshCw size={14} /></button>
          <button
            data-testid="send-all-reminders"
            onClick={async () => {
              if (!window.confirm(`Send WhatsApp reminders to all ${items.length} guests?`)) return;
              try {
                const { data } = await api.post(`/staff/reservations/reminders/send?days=${days}`);
                if (data.sent > 0) toast.success(`Sent ${data.sent} of ${data.total} · ${data.failed} failed`);
                else if (data.total === 0) toast(`No confirmed bookings to remind.`);
                else toast.error(`0 sent · ${data.failed} failed. Check /staff/whatsapp-log.`);
                if (data.failed > 0 && data.errors?.length) console.warn("Reminder errors:", data.errors);
              } catch (err) {
                console.error(err);
                toast.error(err?.response?.data?.detail || "Send failed");
              }
            }}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--brass))] rounded text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={14} /> Send all via WhatsApp
          </button>
        </div>
      </div>

      <div className="mt-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && items.length === 0 && (
          <div data-testid="reminders-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <CalendarClock size={22} className="mx-auto text-[hsl(var(--brass))]" />
            <div className="mt-3">No confirmed bookings {label}. Time for a cup of coffee.</div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {items.map((r) => {
              const wa = reminderMessage(r, window.location.origin);
              const wasSent = !!sent[r.id];
              return (
                <li key={r.id} data-testid={`reminder-${r.id}`} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="text-lg font-semibold">{r.name}</div>
                      <div className="text-sm text-[hsl(var(--ink-soft))]">
                        {r.time} · {r.party_size} pax
                      </div>
                      {wasSent && (
                        <span data-testid={`sent-${r.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))] rounded">Reminder sent</span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
                      {r.phone} · {r.email}
                      {r.occasion && <> · <span className="text-[hsl(var(--ink))]">{r.occasion}</span></>}
                    </div>
                    {r.notes && (
                      <div className="mt-1 text-sm italic text-[hsl(var(--ink-soft))]">"{r.notes}"</div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {wa ? (
                      <a
                        data-testid={`whatsapp-${r.id}`}
                        href={wa}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => markSent(r.id)}
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--brass))] text-[hsl(var(--forest-2))] font-semibold"
                      >
                        <MessageCircle size={12} /> WhatsApp reminder
                      </a>
                    ) : (
                      <span className="text-xs text-[hsl(var(--ink-soft))] italic px-3 py-2">No phone on file</span>
                    )}
                    <Link
                      to={`/staff/reservations/${r.id}/print`}
                      data-testid={`print-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))]"
                    >
                      <Printer size={12} /> Print
                    </Link>
                    {!wasSent && (
                      <button
                        data-testid={`mark-sent-${r.id}`}
                        onClick={() => markSent(r.id)}
                        className="text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))]"
                      >
                        Mark sent
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
